import { AnimatePresence, motion } from 'framer-motion';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { DEVICE } from '@hero/constants';
import { useQueries } from '@hero/hooks';
import { InviteFinish, InviteInfoForm } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useAcceptInvite, useGetInvite } from '@hero/services/organization-service/hooks';
import { storage } from '@hero/utils';

import InviteLayout from './layout';

const MotionDiv = styled(motion.div)`
  width: fit-content;

  ${DEVICE.mobile.max} {
    width: 100%;
  }
`;

export default function SignUpPage() {
  const { t } = useTranslation();

  const { queries } = useQueries();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [authInfo, setAuthInfo] = useState(null);
  const [signUpData, setSignUpData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  });

  const { mutateAsync: mutateAsyncAcceptInvite, isLoading: isLoadingAcceptInvite } =
    useAcceptInvite({
      onSuccess: async responseData => {
        await storage.removeUserToken();
        setActiveStep(activeStep + 1);
        setAuthInfo({
          token: responseData.token,
          organizationCode: data?.organization?.code
        });
      }
    });

  const { data, isLoading } = useGetInvite(
    { token: queries?.inviteToken },
    {
      onSuccess: successData => {
        if (successData?.invite?.isExpired) {
          navigate(ROUTE_PATHS.INVITE_EXPIRED());
          return;
        }
        setSignUpData({ ...signUpData, email: successData?.invite?.invitee.email });
      },
      onError: error => {
        if (error?.response?.status === 404) {
          navigate(ROUTE_PATHS.NOT_FOUND);
        }
      },
      enabled: !!queries?.inviteToken
    }
  );

  const steps = [
    {
      id: 'contactInfo',
      label: 'Contact Info',
      Component: InviteInfoForm,
      componentProps: {
        organizationName: data?.organization?.displayName,
        initialValues: { email: signUpData.email },
        onSubmit: data => {
          mutateAsyncAcceptInvite({
            body: {
              token: queries?.inviteToken,
              firstName: data.firstName,
              lastName: data.lastName,
              password: data.password
            }
          });
        },
        primaryButton: {
          children: t('completeSignUp'),
          disabled: isLoadingAcceptInvite,
          loading: isLoadingAcceptInvite
        }
      },
      step: 0
    },
    {
      id: 'finish',
      label: 'Finish',
      Component: InviteFinish,
      componentProps: {
        organizationName: data?.organization?.displayName,
        authInfo
      },
      step: 1
    }
  ];

  const Component = useMemo(
    () => steps?.[activeStep]?.Component,
    [activeStep, data, signUpData, isLoadingAcceptInvite]
  );
  const componentProps = useMemo(
    () => steps?.[activeStep]?.componentProps,
    [activeStep, data, signUpData, isLoadingAcceptInvite]
  );

  return (
    <InviteLayout isLoading={isLoading}>
      <AnimatePresence initial={false} mode="wait">
        <MotionDiv
          key={activeStep}
          variants={{
            initial: { opacity: 0 },
            enter: { opacity: 1, transition: { duration: 0.4 } },
            exit: { opacity: 0, transition: { duration: 0.4 } }
          }}
          initial="initial"
          animate="enter"
          exit="exit"
        >
          <Component {...componentProps} />
        </MotionDiv>
      </AnimatePresence>
    </InviteLayout>
  );
}
